import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

interface UseHashScrollOptions {
  delay?: number;
  behavior?: 'auto' | 'instant' | 'smooth';
  /* Re-run the scroll effect when this changes (e.g. after switching a tab so target refs become available) */
  resetKey?: string | number;
}

export const getColorGroupId = (txt: string) => txt.replace(/\s+/g, '-').toLowerCase();

export function useHashScroll({
  delay = 0,
  behavior = 'smooth',
  resetKey,
}: UseHashScrollOptions = {}) {
  const router = useRouter();

  const refs = useRef<Record<string, HTMLElement | null>>({});

  const register = (id: string) => (el: HTMLElement | null) => {
    const hashId = getColorGroupId(id);
    refs.current[hashId] = el;
  };

  useEffect(() => {
    const hash = router.asPath.split('#')[1];
    if (!hash) return;

    const el = refs.current[hash];

    const timeoutId = setTimeout(() => {
      if (el) {
        el.scrollIntoView({
          behavior,
          block: 'start',
        });

        el.focus({ preventScroll: true });
      }
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [router.asPath, delay, behavior, resetKey]);

  return register;
}
